import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  IconButton,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { environment } from '../../../environments/environment';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InfoIcon from '@mui/icons-material/Info';
import { formatPriceFromCents } from '../../Utils/UtilsFunctions';

const FeesDialog = ({
  opentaxes,
  setOpentaxes,
  orderType,
  backendTax,
  dialogType = 'tax', // 'tax' or 'delivery'
  storeData = null, // Store data for delivery fees
  taxesText = null,
}) => {
  const [t] = useTranslation();
  const handleClose = () => {
    setOpentaxes(false);
  };
  
  // Tax calculation
  const taxAmount = parseFloat((backendTax || 0).toString());
  const formattedTax = isNaN(taxAmount) ? '0.00' : taxAmount.toFixed(2);

  const serviceCharge = parseFloat((storeData?.service_charge || 0).toString());
  const doordashFee = parseFloat(
    (storeData?.doordash_delivery_fees || 0).toString()
  );

  const totalDeliveryFees = serviceCharge + doordashFee;
  const formattedDeliveryTotal = isNaN(totalDeliveryFees)
    ? '0.00'
    : totalDeliveryFees.toFixed(2);

  const isDeliveryDialog = dialogType === 'delivery';
  const dialogTitle = isDeliveryDialog
    ? t('basket.deliveryFees')
    : t('basket.estTaxes');
  const mainAmount = isDeliveryDialog ? formattedDeliveryTotal : formattedTax;

  return (
    <Dialog
      open={opentaxes}
      onClose={handleClose}
      aria-labelledby="fees-dialog-title"
      aria-describedby="fees-dialog-description"
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 200, // Higher than CartDrawer (1400) to ensure it appears on top
      }}
      slotProps={{
        backdrop: {
          sx: {
            zIndex: (theme) => theme.zIndex.modal + 199, // Backdrop should be below the dialog content
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          zIndex: (theme) => theme.zIndex.modal + 200, // Higher than CartDrawer (1400) and backdrop (1499) to ensure it appears on top
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        id="fees-dialog-title"
        sx={{
          background: `linear-gradient(135deg, ${environment.mainColor}15, ${environment.mainColor}25)`,
          borderBottom: `1px solid ${environment.mainColor}20`,
          position: 'relative',
          pb: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ReceiptIcon
              sx={{
                color: environment.mainColor,
                fontSize: '28px',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#172B4D',
                fontSize: '20px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              {dialogTitle}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: `${environment.mainColor}15`,
                color: environment.mainColor,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Content */}
      <DialogContent sx={{ p: 3 }}>
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Main Amount Display */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mt: 2,
              background: `linear-gradient(135deg, ${environment.mainColor}08, ${environment.mainColor}12)`,
              border: `1px solid ${environment.mainColor}20`,
              borderRadius: '12px',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#666',
                fontSize: '14px',
                fontWeight: 500,
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {isDeliveryDialog ? 'Total Delivery Fees' : t('basket.estTaxes')}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: environment.mainColor,
                fontSize: '32px',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              {environment.currency}
              {!isDeliveryDialog
                ? mainAmount
                : formatPriceFromCents(mainAmount)}
            </Typography>
          </Paper>

          {/* Taxes text (from API) - only for taxes dialog */}
          {!isDeliveryDialog && taxesText && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: '#495057',
                  fontSize: '15px',
                  mb: 1,
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                Tax details
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#495057',
                  fontSize: '14px',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {taxesText}
              </Typography>
            </Box>
          )}

          {/* Breakdown Section for Delivery Fees */}
          {isDeliveryDialog && (
            <Box
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: '#495057',
                  fontSize: '16px',
                  mb: 2,
                  fontFamily: 'Jost, sans-serif',
                }}
              >
                Fee Breakdown:
              </Typography>

              {/* {onlineFee > 0 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#6c757d', fontSize: '14px' }}
                  >
                    Online Fee:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#495057', fontWeight: 500 }}
                  >
                    {environment.currency}
                    {formatPriceFromCents(onlineFee)}
                  </Typography>
                </Box>
              )} */}

              {serviceCharge > 0 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#6c757d', fontSize: '14px' }}
                  >
                    Service Charge:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#495057', fontWeight: 500 }}
                  >
                    {environment.currency}
                    {formatPriceFromCents(serviceCharge)}
                  </Typography>
                </Box>
              )}

              {doordashFee > 0 && (
                <Box
                  display="flex"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#6c757d', fontSize: '14px' }}
                  >
                    DoorDash Delivery Fee:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#495057', fontWeight: 500 }}
                  >
                    {environment.currency}
                    {formatPriceFromCents(doordashFee)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Information Section */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1.5}>
              <InfoIcon
                sx={{
                  color: '#6c757d',
                  fontSize: '20px',
                  mt: 0.2,
                }}
              />
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#495057',
                    fontSize: '14px',
                    lineHeight: 1.5,
                    fontFamily: 'Jost, sans-serif',
                  }}
                >
                  {isDeliveryDialog
                    ? 'Delivery fees may vary based on your location, order size, and current demand.'
                    : t('basket.finalisedTaxWillBeShownOnYourOrderReceipt')}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Additional Info */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Chip
              label={isDeliveryDialog ? 'Current Fees' : 'Estimated Amount'}
              size="small"
              sx={{
                backgroundColor: `${environment.mainColor}15`,
                color: environment.mainColor,
                fontWeight: 600,
                alignSelf: 'flex-start',
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: '#6c757d',
                fontSize: '12px',
                fontStyle: 'italic',
              }}
            >
              {isDeliveryDialog
                ? 'These fees help cover the cost of delivery and service'
                : 'Tax rates may vary based on your location and order type'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      {/* Actions */}
      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            background: `linear-gradient(135deg, ${environment.mainColor}, ${environment.mainColor}dd)`,
            color: 'white',
            borderRadius: '8px',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '14px',
            fontFamily: 'Jost, sans-serif',
            letterSpacing: '0.3px',
            boxShadow: `0 2px 8px ${environment.mainColor}30`,
            '&:hover': {
              background: `linear-gradient(135deg, ${environment.mainColor}dd, ${environment.mainColor})`,
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${environment.mainColor}50`,
            },
            '&:active': {
              transform: 'translateY(0px)',
              boxShadow: `0 1px 4px ${environment.mainColor}30`,
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            minHeight: '36px',
            alignSelf: 'end',
          }}
        >
          {t('basket.oK')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeesDialog;
